/**
 * Shared helpers for rendering the "this is a linked-doc stub" affordance
 * across every view. Three states map to icon + label + class:
 *
 *   loaded     — content is composited; click to open the doc standalone.
 *   loading    — sync still in flight; visual hint that content will appear.
 *   no-access  — user has no DEK; click triggers the request-access flow.
 *   gone       — no account owns the map any more (deleted, or moved to a
 *                device): nothing to request; the link can be removed.
 *   nested     — a link inside linked content (never expanded); click opens it.
 *   conflict   — the map links itself, or its node ids already appear here
 *                (a copy of a map shown in the same place); click opens it.
 *   offline    — the server can't be reached; the saved copy is shown
 *                read-only until the connection comes back.
 *
 * Returns a small descriptor each view turns into its own DOM. Keeping the
 * mapping in one place means a future icon/label swap touches one file.
 */

import { t } from "../../../shared/i18n/index.js";

// Texts are keys, read when a badge is rendered. Each title has a variant
// for a linked map without a known name ("…Unnamed").
const STATE = {
    loaded:    { icon: "🔗",  labelKey: "views.linked.label.linked",    className: "linked-badge",                            titleKey: "views.linked.loaded" },
    loading:   { icon: "↻",  labelKey: "views.linked.label.loading",   className: "linked-badge linked-badge--loading",      titleKey: "views.linked.loading" },
    "no-access":      { icon: "🔒", labelKey: "views.linked.label.noAccess",  className: "linked-badge linked-badge--no-access",     titleKey: "views.linked.noAccess" },
    "pending-access": { icon: "⏳", labelKey: "views.linked.label.requested", className: "linked-badge linked-badge--pending-access", titleKey: "views.linked.pending" },
    gone:      { icon: "⌀",  labelKey: "views.linked.label.gone",      className: "linked-badge linked-badge--gone",         titleKey: "views.linked.gone" },
    nested:    { icon: "🔗", labelKey: "views.linked.label.linked",    className: "linked-badge linked-badge--nested",       titleKey: "views.linked.nested" },
    offline:   { icon: "⚡", labelKey: "views.linked.label.offline",   className: "linked-badge linked-badge--offline",      titleKey: "views.linked.offline" },
    conflict:  { icon: "⚠",  labelKey: "views.linked.label.conflict",  className: "linked-badge linked-badge--conflict",     titleKey: "views.linked.conflict" },
};

/**
 * Resolve the rendering descriptor for a stub node.
 * @param {Object} node - Composited tree node with `_linkedState`.
 * @returns {{ state, icon, label, className, title, isLinkedStub }}
 */
export function getLinkedBadge(node) {
    if (!node || !node.linkedDocId) {
        return { isLinkedStub: false };
    }
    const state = node._linkedState || (node._linkedDocLoaded ? "loaded" : "loading");
    const cfg = STATE[state] || STATE.loading;
    const name = node.linkedDocTitle || node.name;
    return {
        isLinkedStub: true,
        state,
        icon: cfg.icon,
        label: t(cfg.labelKey),
        className: cfg.className,
        title: name ? t(cfg.titleKey, { name }) : t(`${cfg.titleKey}Unnamed`),
        docId: node.linkedDocId,
    };
}

/**
 * Class string to apply to the node container element so `.linked-stub`
 * CSS rules (dashed left-border + tint + cursor) take effect. Caller may
 * append this to its own class list.
 * @param {Object} node
 * @returns {string} space-separated class names, or "" when not a stub.
 */
export function linkedStubContainerClasses(node) {
    if (!node || !node.linkedDocId) return "";
    const state = node._linkedState || (node._linkedDocLoaded ? "loaded" : "loading");
    return `linked-stub linked-stub--${state}`;
}
